import { useEffect, useState, useCallback, useRef } from "react";
import { useApiCache } from "./ApiCacheContext";
import { ServiceResponse } from "../../client/openApiFetchWrapper";

type UseCachedApiOptions = {
  // Time in seconds before cached data is considered stale
  staleTime?: number;
  // Whether to refetch on mount even if data is cached
  refetchOnMount?: boolean;
  // Whether to enable the query
  enabled?: boolean;
};

type UseCachedApiResult<T> = {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isFetching: boolean;
};

export type BasicApiFunction<T> = () => Promise<T>;
export type StandardApiFunction<T> = () => Promise<ServiceResponse<T>>;

type ApiFunction<T> = BasicApiFunction<T> | StandardApiFunction<T>;

/**
 * Hook for consuming cached API data with automatic refetching
 * @param cacheKey - Unique key for this API call (should include parameters)
 * @param apiFn - The async function that fetches the data
 * @param options - Configuration options
 */
export const useCachedApi = <T>(
  cacheKey: string,
  apiFn: ApiFunction<T>,
  options: UseCachedApiOptions = {}
): UseCachedApiResult<T> => {
  const { staleTime, refetchOnMount = true, enabled = true } = options;
  const { getCache, setCache, subscribe } = useApiCache();

  const [data, setData] = useState<T | null>(() => {
    const cached = getCache<T>(cacheKey);
    return cached?.data || null;
  });

  const [isLoading, setIsLoading] = useState<boolean>(() => {
    const cached = getCache<T>(cacheKey);
    return cached ? cached.isLoading : true;
  });

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(() => {
    const cached = getCache<T>(cacheKey);
    return cached?.error || null;
  });

  const mountedRef = useRef(true);
  const fetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (fetchingRef.current || !enabled) return;

    fetchingRef.current = true;
    setIsFetching(true);

    // If no data exists yet, show loading state
    if (data === null) {
      setIsLoading(true);
    }

    setCache(cacheKey, data, true, null);

    try {
      const result = await apiFn();
      const castResult = result as Awaited<T>;
      let resolvedData: T | null = null;

      // Handle api calls that return data in the shape of { data, error }
      if (result && typeof result === "object" && "data" in result && "error" in result) {
        const apiResult = result as { data: T | null; error: { message?: string } | null };
        if (apiResult.error) {
          throw new Error(apiResult.error.message || "API error");
        }
        resolvedData = apiResult.data;
      } else {
        resolvedData = castResult;
      }

      if (mountedRef.current) {
        setData(resolvedData);
        setError(null);
        setCache(cacheKey, resolvedData, false, null);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));

      if (mountedRef.current) {
        setError(error);
        setCache(cacheKey, null, false, error);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setIsFetching(false);
      }
      fetchingRef.current = false;
    }
  }, [cacheKey, apiFn, data, enabled, setCache]);

  // Subscribe to cache updates from other components
  useEffect(() => {
    const unsubscribe = subscribe<T>(cacheKey, (newData, newIsLoading, newError) => {
      if (mountedRef.current) {
        setData(newData);
        setIsLoading(newIsLoading);
        setError(newError);
      }
    });

    return unsubscribe;
  }, [cacheKey, subscribe]);

  // Initial fetch or refetch logic
  useEffect(() => {
    if (!enabled) return;

    const cached = getCache<T>(cacheKey);

    if (!cached) {
      // No cache, fetch immediately
      fetchData();
    } else if (refetchOnMount) {
      // Always refetch in background on mount, regardless of staleness
      fetchData();
    }
  }, [cacheKey, enabled]); // Intentionally limited dependencies - won't trigger on re-renders

  // Auto-refetch when staleTime has elapsed while component is still mounted
  useEffect(() => {
    if (!enabled || !staleTime) return;

    const staleTimeMs = staleTime * 1000; // Convert seconds to milliseconds

    const checkStaleAndRefetch = () => {
      const cached = getCache<T>(cacheKey);
      if (cached && Date.now() - cached.timestamp > staleTimeMs) {
        fetchData();
      }
    };

    // Check immediately, then set up interval
    const timerId = setInterval(checkStaleAndRefetch, staleTimeMs);

    return () => {
      clearInterval(timerId);
    };
  }, [cacheKey, enabled, staleTime, getCache, fetchData]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    isFetching,
  };
};

/**
 * Helper function to generate cache keys from function name and parameters
 */
export const generateCacheKey = (fnName: string, ...params: unknown[]): string => {
  const paramString = params.length > 0 ? JSON.stringify(params) : "";
  return `${fnName}${paramString ? `:${paramString}` : ""}`;
};
